import { Formik, FormikValues } from "formik";

import { useGetPricingByIdQuery, useUpdatePricingMutation } from "@sso/apis/pricing";
import { useGetSoftwareLicenceById } from "@sso/apis/softwareLicences/hooks";
import { useAppSelector } from "@sso/hooks/useRedux";
import { closeDialog, selectDialog } from "@sso/store/slices/dialog";
import { selectPricingSoftwareLicenceId } from "@sso/store/slices/pricing";
import {
  LicenceType,
  PricingKeys,
  PricingRuleKeys,
  PricingUpdateRequest,
  SoftwareLicenceSummaryKeys,
} from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import { DialogActionButtons, DialogBody, DialogHeader } from "../components";
import AddCurrencyPricingForm from "./addCurrenciesForm";
import { addCurrencyValidation } from "./schema";

const AddPricingCurrencyDialog = () => {
  const { id } = useAppSelector(selectDialog);
  if (!id) {
    throw new Error("PricingEditDialog: id is null");
  }

  const { data } = useGetPricingByIdQuery(id);
  const [updatePricing, { isLoading: updateLoading }] = useUpdatePricingMutation();
  const softwareLicenceId = useAppSelector(selectPricingSoftwareLicenceId);
  const softwareLicence = useGetSoftwareLicenceById(softwareLicenceId);

  const rules = data?.pricingRules
    ?.filter(x => x.currency?.code === "GBP")
    .map(rule => ({
      [PricingRuleKeys.MIN_RANGE]: rule[PricingRuleKeys.MIN_RANGE],
      [PricingRuleKeys.MAX_RANGE]: rule[PricingRuleKeys.MAX_RANGE],
      [PricingRuleKeys.BILLABLE_OPERATION_ID]: rule[PricingRuleKeys.BILLABLE_OPERATION_ID],
    }));

  const addCurrenciesForm = {
    initialValues: {
      currencyCode: "",
      pricingRules: rules,
      apiPricingRules: [],
      licenceType: softwareLicence ? softwareLicence[SoftwareLicenceSummaryKeys.LICENCE_TYPE] : "",
    },
    onSubmit: (values: FormikValues) => {
      const pricingRules = values.pricingRules.map((rule: any) => ({
        [PricingRuleKeys.MIN_RANGE]: rule[PricingRuleKeys.MIN_RANGE],
        [PricingRuleKeys.MAX_RANGE]: rule[PricingRuleKeys.MAX_RANGE],
        [PricingRuleKeys.BILLABLE_OPERATION_ID]: rule[PricingRuleKeys.BILLABLE_OPERATION_ID],
        [PricingRuleKeys.PRICE]: rule[PricingRuleKeys.PRICE],
        [PricingRuleKeys.CURRENCY_CODE]: values.currencyCode,
      }));

      const apiPricingRules = values.apiPricingRules?.reduce((acc: any, curr: any) => {
        const pricingRules = curr.pricingRules.map((rule: any) => {
          return { ...rule, billableOperationId: curr.billableOperationId, currencyCode: values.currencyCode };
        });
        return [...acc, ...pricingRules];
      }, []);

      const existingRules =
        data?.pricingRules?.map((rule: any) => ({
          [PricingRuleKeys.MIN_RANGE]: rule[PricingRuleKeys.MIN_RANGE],
          [PricingRuleKeys.MAX_RANGE]: rule[PricingRuleKeys.MAX_RANGE],
          [PricingRuleKeys.BILLABLE_OPERATION_ID]: rule[PricingRuleKeys.BILLABLE_OPERATION_ID],
          [PricingRuleKeys.PRICE]: rule[PricingRuleKeys.PRICE],
          [PricingRuleKeys.CURRENCY_CODE]: rule.currency.code,
        })) || [];

      const rulesToAdd = values.licenceType === LicenceType.API ? apiPricingRules : pricingRules;
      const allRules = [...existingRules, ...rulesToAdd];

      const request = {
        [PricingKeys.PRICING_RULES]: allRules,
        softwareLicenceId: softwareLicenceId,
        [PricingKeys.PRICING_ID]: data?.pricingId,
        [PricingKeys.DATE_OF_EFFECT]: data?.dateOfEffect,
      } as PricingUpdateRequest;

      updatePricing(request)
        .unwrap()
        .then(() => {
          closeDialog();
        });
    },
  };

  return (
    <>
      <DialogHeader title={"Add prices for currency"} />
      {softwareLicence && rules && rules.length > 0 && data && (
        <Formik
          onSubmit={addCurrenciesForm.onSubmit}
          initialValues={addCurrenciesForm.initialValues}
          validationSchema={addCurrencyValidation}
          validateOnMount
        >
          {({ handleSubmit, errors }) => (
            <CustomForm onSubmit={handleSubmit} padding="1rem 0" width="100%" overflow="hidden">
              <DialogBody>
                <AddCurrencyPricingForm
                  pricing={data}
                  licenceType={softwareLicence[SoftwareLicenceSummaryKeys.LICENCE_TYPE]}
                />
              </DialogBody>
              <DialogActionButtons loading={updateLoading} buttonTitle={"Add"} />
            </CustomForm>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddPricingCurrencyDialog;
